import {
  AdminCaseListSlide,
  AdminSlideList,
  Block,
  Case,
  CaseSlide,
  CaseSlideAdmin,
  Slide,
  SlideAdmin,
  Specimen,
  Stain,
} from "pwapi";

import { StainList } from "custom-models/frontLoader";

import { StainsState } from "reducers/frontLoaderReducers";

import { SortedBlock, _compareBlocks } from "./blockUtils";
import { _compareSpecimens } from "./specimenUtils";
import { CaseDetailState } from "../reducers/caseDetailReducer";

type AnySlide = CaseSlide | CaseSlideAdmin | AdminCaseListSlide;

export type SlidesObj = { [key: string]: AnySlide };

export function slideStainLabel(slide: AnySlide, stain: StainList) {
  return `${slide.is_control ? "CL-" : ""}${slide.is_rescan ? "RS-" : ""}${
    stain?.abbreviation ?? ""
  }`;
}

export const slideLabel = (
  slide: AnySlide,
  block?: Block,
  specimen?: Specimen,
  stain?: StainList,
  stainInMiddle?: boolean
) => {
  const blockName = `${specimen?.name ?? ""}${block?.name ?? ""}`;
  const stainName = stain ? slideStainLabel(slide, stain) : "";
  const slideName = slide.slide_number
    ? `${slide.slide_number.match(/^\d/) ? "S" : ""}${slide.slide_number}`
    : "";

  if (!stainName)
    return `${blockName}${blockName && slideName ? "." : ""}${slideName}`;

  if (stainInMiddle)
    return `${blockName}${blockName ? " " : ""}${stainName}${
      slideName ? " " : ""
    }${slideName}`;

  return `${blockName}${blockName && slideName ? "." : ""}${slideName}${
    stainName ? " " : ""
  }${stainName}`;
};

export function caseSlideLabel(
  s: CaseSlide,
  pathCase: Case | CaseDetailState,
  stain?: StainList,
  stainInMiddle?: boolean
) {
  const block = pathCase.blocks?.find((b) => b.id === s.block_obj_id);
  const specimen = pathCase.specimens?.find((s) => s.id === block?.specimen_id);
  return slideLabel(s, block, specimen, stain, stainInMiddle);
}

export const slideDetailLabel = (
  s: Slide | AdminSlideList | SlideAdmin,
  stain?: StainList,
  stainInMiddle?: boolean
) => slideLabel(s, s.block_obj, s.specimen, stain, stainInMiddle);

export type SortedSlideBase = {
  full_name: string;
  full_name_stain: string;
};
export type SortedSlide = AnySlide & SortedSlideBase;

// DO NOT USE THIS FUNCTION
//
// _createCompareSlides() is only exported for testing purposes
export const _createCompareSlides =
  (stains: StainsState) =>
  (a: AnySlide, b: AnySlide): number => {
    const stainA = stains[a.stain_id || 0];
    const stainB = stains[b.stain_id || 0];

    // Stainless Slides are unlikely to ever actually happen outside of tests, but we put stainless Slides first just in case.
    if (stainA && !stainB) return -1;
    if (!stainA && stainB) return 1;

    // Routine Stains take precedent
    if (
      stainA?.category === Stain.category.ROUTINE &&
      stainB?.category !== Stain.category.ROUTINE
    )
      return -1;
    if (
      stainA?.category !== Stain.category.ROUTINE &&
      stainB?.category === Stain.category.ROUTINE
    )
      return 1;

    // Sort by Stain abbreviation first
    if (stainA && stainB && stainA.abbreviation !== stainB.abbreviation)
      return stainA.abbreviation.localeCompare(stainB.abbreviation, "en");

    // Control First, Rescan After, Control + Rescan First
    if (a.is_control && !b.is_control) return -1;
    if (!a.is_control && b.is_control) return 1;
    if (a.is_rescan && !b.is_rescan) return 1;
    if (!a.is_rescan && b.is_rescan) return -1;

    // Sort by slide_number last
    return (
      (a.slide_number || "").localeCompare(b.slide_number || "", "en", {
        numeric: true,
      }) || (a.id || 0) - (b.id || 0)
    );
  };

export const createCompareAdminSlides = (stains: StainsState) => {
  const compareSlides = _createCompareSlides(stains);

  return (a: AdminSlideList, b: AdminSlideList): number => {
    const specimenComp =
      a.specimen && b.specimen
        ? _compareSpecimens(a.specimen, b.specimen)
        : !a.specimen && !b.specimen
        ? 0
        : a.specimen
        ? 1
        : -1;

    const blockComp =
      a.block_obj && b.block_obj
        ? _compareBlocks(a.block_obj, b.block_obj)
        : !a.block_obj && !b.block_obj
        ? 0
        : a.block_obj
        ? 1
        : -1;

    return specimenComp || blockComp || compareSlides(a, b);
  };
};

const _mapSlideToSortedSlide = (
  slide: AnySlide,
  block?: SortedBlock,
  specimen?: Specimen,
  stain?: StainList
): SortedSlide => ({
  ...slide,
  full_name: slideLabel(slide, block, specimen),
  full_name_stain: slideLabel(slide, block, specimen, stain),
});

// DO NOT USE THIS FUNCTION DIRECTLY
//
// sortSlides() is a pure function with no memoization or caching built in.
// Instead, use functions from selectors/selectSortedCaseDetail.ts or hooks/useSortedCase.
// It is preferable to use the selectors when possible.
export const sortSlides = (
  slides: Array<AnySlide>,
  sortedBlocks: SortedBlock[],
  sortedSpecimens: Specimen[],
  stains: StainsState
): SortedSlide[] => {
  const compareSlides = _createCompareSlides(stains);

  const blockIds = sortedBlocks.map((b) => b.id);
  const orphanSlides = (slides || [])
    .filter((s) => !s.block_obj_id || !blockIds.includes(s.block_obj_id))
    .sort(compareSlides)
    .map((slide) =>
      _mapSlideToSortedSlide(
        slide,
        undefined,
        sortedSpecimens.find((s) => s.id === slide.specimen_id),
        stains[slide.stain_id || 0]
      )
    );

  const blockSlides = sortedBlocks
    .map((block) =>
      (slides || [])
        .filter((slide) => slide.block_obj_id === block.id)
        .sort(compareSlides)
        .map((slide) =>
          _mapSlideToSortedSlide(
            slide,
            block,
            sortedSpecimens.find((s) => s.id === slide.specimen_id),
            stains[slide.stain_id || 0]
          )
        )
    )
    .flat();

  return orphanSlides.concat(blockSlides);
};

/*
 * Gets the immediate next slide in a slides list
 * Gets the first slide if slide is at the end of the list
 */
export function getWrapAroundSlide(
  slides: Array<AnySlide>,
  currentSlide: AnySlide
): AnySlide | undefined {
  if (slides && slides.length > 1) {
    const currSlideIndex = slides.findIndex((s) => s.id === currentSlide.id);

    return currSlideIndex + 1 === slides.length
      ? slides[0]
      : slides[currSlideIndex + 1];
  } else {
    return undefined;
  }
}
